import { createContext, useContext, useEffect, useRef } from 'react';
import { activity } from '../lib/api';

const ActivityContext = createContext(null);

// Generate or retrieve session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('activity_session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
    sessionStorage.setItem('activity_session_id', sessionId);
  }
  return sessionId;
};

export const ActivityProvider = ({ children }) => {
  const sessionId = useRef(getSessionId());

  const trackEvent = async (eventType, data = {}) => {
    try {
      await activity.track({
        session_id: sessionId.current,
        event_type: eventType,
        ...data
      });
    } catch (error) {
      // Silently fail - don't disrupt user experience
      console.debug('Activity tracking failed:', error);
    }
  };

  const trackProductView = (product) => {
    trackEvent('product_view', {
      product_id: product.id,
      product_name: product.name,
      product_sku: product.sku,
      product_category: product.category
    });
  };

  const trackSearch = (query, resultsCount) => {
    trackEvent('search', {
      search_query: query,
      search_results_count: resultsCount
    });
  };

  const trackCategoryView = (categoryName) => {
    trackEvent('category_view', {
      category_name: categoryName
    });
  };

  const trackCartAdd = (product) => {
    trackEvent('cart_add', {
      product_id: product.id,
      product_name: product.name,
      product_sku: product.sku,
      product_category: product.category
    });
  };

  const trackPageView = (url, title) => {
    trackEvent('page_view', {
      page_url: url,
      page_title: title
    });
  };

  return (
    <ActivityContext.Provider value={{
      trackProductView,
      trackSearch,
      trackCategoryView,
      trackCartAdd,
      trackPageView,
      sessionId: sessionId.current
    }}>
      {children}
    </ActivityContext.Provider>
  );
};

export const useActivity = () => {
  const context = useContext(ActivityContext);
  if (!context) {
    throw new Error('useActivity must be used within ActivityProvider');
  }
  return context;
};

export default ActivityContext;
